import apiClient from './client';
import type { Enrollment, RegistrationFormData } from '../types';

export interface RegisterResponse {
  success: boolean;
  accessToken: string;
  enrollmentId: string;
}

export interface EnrollmentsFilter {
  edition_id?: string;
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
}

export async function registerUser(data: RegistrationFormData): Promise<RegisterResponse> {
  const response = await apiClient.post<RegisterResponse>('/enrollments', data);
  return response.data;
}

export async function getMyEnrollment(token: string): Promise<Enrollment> {
  const response = await apiClient.get<{ enrollment: Enrollment }>('/enrollment/me', {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data.enrollment;
}

export async function getAdminEnrollments(
  filters: EnrollmentsFilter = {}
): Promise<{ enrollments: Enrollment[]; pagination: { page: number; limit: number; total: number } }> {
  const response = await apiClient.get('/admin/enrollments', {
    params: filters,
  });
  return response.data;
}

export async function getAdminEnrollment(id: string): Promise<Enrollment> {
  const response = await apiClient.get<{ enrollment: Enrollment }>(`/admin/enrollments/${id}`);
  return response.data.enrollment;
}

export async function approveEnrollment(id: string): Promise<void> {
  await apiClient.post(`/admin/enrollments/${id}/approve`);
}

export async function cancelEnrollment(id: string, reason?: string): Promise<void> {
  await apiClient.post(`/admin/enrollments/${id}/cancel`, { reason });
}

export async function markAttended(id: string): Promise<void> {
  await apiClient.post(`/admin/enrollments/${id}/mark-attended`);
}

export async function markNoShow(id: string): Promise<void> {
  await apiClient.post(`/admin/enrollments/${id}/mark-no-show`);
}

export async function resendEmail(
  id: string,
  type: 'confirmation' | 'join_link' | 'reminder' | 'reminder_day_before'
): Promise<void> {
  await apiClient.post(`/admin/enrollments/${id}/resend-email`, { type });
}

export async function deleteEnrollment(id: string): Promise<void> {
  await apiClient.delete(`/admin/enrollments/${id}`);
}

export async function promoteFromWaitlist(id: string): Promise<void> {
  await apiClient.post(`/admin/enrollments/${id}/promote`);
}

export async function adminManualEnroll(data: {
  nome: string;
  cognome: string;
  email: string;
  azienda?: string;
  edition_ids: string[];
  extra_data?: Record<string, string | boolean | number>;
}): Promise<{ success: boolean; enrollments: { enrollmentId: string; accessToken: string }[] }> {
  const response = await apiClient.post('/admin/enrollments/manual', data);
  return response.data;
}

// ─── Users ────────────────────────────────────────────────────────────────────

export interface AdminUser {
  id: string;
  nome: string;
  cognome: string;
  email: string;
  azienda?: string;
  created_at: string;
  enrollment_count: number;
  active_enrollment_count: number;
}

export interface AdminUserEnrollment {
  id: string;
  status: string;
  registered_at: string;
  cancelled_at?: string;
  attended_confirmed_at?: string;
  waitlisted_at?: string;
  access_token: string;
  edition_id: string;
  edition_label: string;
  scheduled_at: string;
  course_id: string;
  course_name: string;
}

export async function getAdminUsers(
  params: { search?: string; page?: number; limit?: number } = {}
): Promise<{ users: AdminUser[]; pagination: { page: number; limit: number; total: number } }> {
  const response = await apiClient.get('/admin/users', { params });
  return response.data;
}

export async function getAdminUser(
  id: string
): Promise<{ user: AdminUser; enrollments: AdminUserEnrollment[] }> {
  const response = await apiClient.get(`/admin/users/${id}`);
  return response.data;
}

export async function updateAdminUser(
  id: string,
  data: { nome?: string; cognome?: string; email?: string; azienda?: string | null }
): Promise<{ user: AdminUser }> {
  const response = await apiClient.put(`/admin/users/${id}`, data);
  return response.data;
}
